// 首頁活動輪播（移植自切版 js/promotion-carousel.js）
// 自動前進、hover／觸控／focus／不在畫面內時暫停，手動操作後暫停 8 秒；卡片以 transform 循環，不複製 DOM
// host 為 .new-events-div，內含 .list（卡片）與 .game-next／.game-prev 箭頭；回傳釋放函式
export function initPromotionCarousel(host) {
  const strip = host.querySelector(':scope > .list')
  if (!strip) return () => {}
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  strip.dataset.promoCarousel = 'ready'
  strip.setAttribute('tabindex', '0')
  strip.setAttribute('role', 'region')

  let timer = 0
  let motion = null
  let snapTimer = 0
  let nativeScroll = false
  let measuredWidth = 0
  let visible = false
  let hovered = false
  let touching = false
  let manualUntil = 0
  const interval = 3200
  const cards = [...strip.querySelectorAll(':scope > .new-swiper-slide')]
  let layout = []
  let period = 0
  let looping = false

  // 中斷時停在目前畫面位置，觸控不會跳回動畫開始前的位置
  const cancelMotion = () => {
    if (!motion) return
    const current = motion
    const transform = new DOMMatrixReadOnly(getComputedStyle(current.anchor).transform)
    const offset = current.from + current.anchorShift - transform.m41
    motion = null
    current.animations.forEach(animation => animation.cancel())
    strip.removeAttribute('data-promo-moving')
    wrapCards(offset)
    strip.scrollLeft = offset
    rebase()
  }
  const paused = () => reducedMotion.matches || document.hidden || !visible || hovered || touching || host.contains(document.activeElement)
  const schedule = (delay = interval) => {
    clearTimeout(timer)
    if (!paused()) timer = setTimeout(advance, delay)
  }
  // 只把已經移出左邊的卡片用 transform 接到最後面
  const wrapCards = offset => {
    layout.forEach(item => {
      const shift = looping && item.end <= offset + 0.01 ? period : 0
      if (shift === item.shift) return
      item.shift = shift
      item.card.style.transform = shift ? `translate3d(${shift}px, 0, 0)` : ''
    })
  }
  const rebase = () => {
    if (!looping || strip.scrollLeft < period - 0.5) return
    const offset = Math.max(0, strip.scrollLeft - period)
    wrapCards(offset)
    strip.scrollLeft = offset
  }
  const measure = () => {
    const width = strip.getBoundingClientRect().width
    // 圖片載入會改變高度，寬度沒變就不重置
    if (layout.length && Math.abs(width - measuredWidth) < 0.5) return
    measuredWidth = width
    cancelMotion()
    clearTimeout(snapTimer)
    nativeScroll = false
    cards.forEach(card => { card.style.transform = '' })
    strip.scrollLeft = 0
    const left = strip.getBoundingClientRect().left
    layout = cards.map(card => {
      const bounds = card.getBoundingClientRect()
      return { card, start: bounds.left - left, end: bounds.right - left, shift: 0 }
    })
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0
    const last = layout[layout.length - 1]
    period = (last ? last.end : 0) + gap
    looping = cards.length > 1 && period - gap > strip.clientWidth + 2
    schedule()
  }
  const move = (target, manual = false) => {
    cancelMotion()
    clearTimeout(snapTimer)
    nativeScroll = false
    const from = strip.scrollLeft
    const duration = reducedMotion.matches ? 0 : manual ? 400 : 500
    // 往回切換時先把需要的卡片放好，只有畫面外的卡片會在頭尾之間移動
    wrapCards(Math.min(from, target))
    const finish = () => {
      const current = motion
      motion = null
      if (current) current.animations.forEach(animation => animation.cancel())
      strip.removeAttribute('data-promo-moving')
      wrapCards(target)
      strip.scrollLeft = target
      rebase()
      schedule()
    }
    if (!duration || Math.abs(target - from) < 0.5) return finish()
    strip.setAttribute('data-promo-moving', '')
    const animations = layout.map(item => item.card.animate([
      { transform: `translate3d(${item.shift}px, 0, 0)` },
      { transform: `translate3d(${item.shift + from - target}px, 0, 0)` }
    ], { duration, easing: 'cubic-bezier(.25, .1, .35, 1)', fill: 'both' }))
    const current = { from, anchor: layout[0].card, anchorShift: layout[0].shift, animations }
    motion = current
    animations[0].finished.then(() => {
      if (motion === current) finish()
    }).catch(() => {}) // 觸控／resize 中斷屬正常取消
  }
  const stops = () => [...layout.map(item => item.start), period]
  function advance() {
    if (paused()) return
    const wait = manualUntil - performance.now()
    if (wait > 0) return schedule(wait + 100)
    if (!looping) return
    const nextItem = layout.find(item => item.start > strip.scrollLeft + 2)
    move(nextItem ? nextItem.start : period)
  }
  const manualPause = () => {
    manualUntil = performance.now() + 8000
    cancelMotion()
    schedule(8000)
  }
  const settleTouch = () => {
    clearTimeout(snapTimer)
    if (!nativeScroll || !window.matchMedia('(max-width: 767px)').matches) return
    snapTimer = setTimeout(() => {
      if (touching || motion || !looping) return
      const target = stops().reduce((nearest, point) => Math.abs(point - strip.scrollLeft) < Math.abs(nearest - strip.scrollLeft) ? point : nearest)
      if (Math.abs(target - strip.scrollLeft) > 1) move(target, true)
    }, 160)
  }
  // 箭頭 class 名稱是反的：game-next 是往左
  const manualStep = (event, step) => {
    event.preventDefault()
    manualPause()
    if (!looping) return
    const points = stops()
    const target = step > 0
      ? points.find(x => x > strip.scrollLeft + 2)
      : points.slice().reverse().find(x => x < strip.scrollLeft - 2)
    if (target !== undefined) move(target, true)
  }

  const listeners = []
  const on = (target, type, handler, options) => {
    target.addEventListener(type, handler, options)
    listeners.push(() => target.removeEventListener(type, handler, options))
  }
  on(host, 'pointerenter', event => {
    if (event.pointerType !== 'mouse') return
    hovered = true
    clearTimeout(timer)
  })
  on(host, 'pointerleave', event => {
    if (event.pointerType !== 'mouse') return
    hovered = false
    schedule()
  })
  on(host, 'focusin', () => { clearTimeout(timer) })
  on(host, 'focusout', () => setTimeout(() => schedule(), 0))
  on(strip, 'pointerdown', () => { touching = true; manualPause(); nativeScroll = true }, { passive: true })
  const release = () => {
    if (!touching) return
    touching = false
    manualPause()
    settleTouch()
  }
  on(window, 'pointerup', release, { passive: true })
  on(window, 'pointercancel', release, { passive: true })
  on(strip, 'wheel', () => { manualPause(); nativeScroll = true }, { passive: true })
  on(strip, 'keydown', event => {
    if (event.key === 'ArrowLeft') return manualStep(event, -1)
    if (event.key === 'ArrowRight') return manualStep(event, 1)
    manualPause()
  })
  on(strip, 'scroll', () => {
    if (motion) return
    wrapCards(strip.scrollLeft)
    rebase()
    settleTouch()
  }, { passive: true })
  ;[['.game-next', -1], ['.game-prev', 1]].forEach(([selector, step]) => {
    const control = host.querySelector(selector)
    if (control) on(control, 'click', event => manualStep(event, step))
  })
  const reset = () => { cancelMotion(); schedule() }
  on(document, 'visibilitychange', reset)
  if (reducedMotion.addEventListener) on(reducedMotion, 'change', reset)

  const intersectionObserver = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.35
    schedule()
  }, { threshold: [0, 0.35] })
  intersectionObserver.observe(strip)
  const resizeObserver = new ResizeObserver(measure)
  resizeObserver.observe(strip)
  schedule()

  return () => {
    clearTimeout(timer)
    clearTimeout(snapTimer)
    if (motion) motion.animations.forEach(animation => animation.cancel())
    motion = null
    intersectionObserver.disconnect()
    resizeObserver.disconnect()
    listeners.forEach(off => off())
    cards.forEach(card => { card.style.transform = '' })
    strip.removeAttribute('data-promo-moving')
    delete strip.dataset.promoCarousel
  }
}
